import { useEffect, useId, useRef } from 'react';
import {
  LoginShell,
  PRODUCT_TAGLINES,
  ProductLockup,
  traceButtonClass,
  useDestination,
  type DestinationContext,
} from '@traceio-org/auth-ui';
import {
  PRODUCTS,
  hubUrl,
  type OrgRole,
  type PortalRole,
  type StaffRole,
} from '@traceio-org/platform-contracts';

/** The exact banner text: this page must never be mistaken for the product. */
export const FIXTURE_BANNER = 'Development destination: navigation fixture, not the product';
/** The banner of the hosted dev skeleton (VITE_HOSTED_PREVIEW=true). */
export const PREVIEW_BANNER = 'Preview — this product is not live yet';

const ROLE_LABELS: Record<OrgRole | PortalRole | StaffRole, string> = {
  org_owner: 'Owner',
  org_admin: 'Administrator',
  billing_admin: 'Billing administrator',
  member: 'Member',
  help_desk: 'Help desk',
  portal_admin: 'Portal administrator',
  portal_user: 'Portal user',
  platform_admin: 'Platform admin',
  support_operator: 'Support operator',
  support_viewer: 'Support viewer',
  billing_operator: 'Billing operator',
};

/** The organization roles the account hub's settings admit (apps/account-hub settings routes). */
const ACCOUNT_MANAGER_ROLES: ReadonlySet<OrgRole> = new Set<OrgRole>([
  'org_owner',
  'org_admin',
  'billing_admin',
]);

function describeContext(context: DestinationContext): { organization: string; roles: string } {
  switch (context.type) {
    case 'employee':
      return {
        organization: context.organization.displayName,
        roles: context.roles.map((role) => ROLE_LABELS[role]).join(', '),
      };
    case 'portal':
      return {
        organization: `${context.customerAccount.displayName}, customer of ${context.providerOrganization.displayName}`,
        roles: ROLE_LABELS[context.role],
      };
    case 'staff':
      return { organization: 'Trace staff', roles: ROLE_LABELS[context.staffRole] };
  }
}

/**
 * What a navigation fixture shows once DestinationGate confirmed access: unmistakably not the product, and
 * exactly what platform-api confirmed (the person, the organization and roles of the context) plus the
 * path it was opened with, so deep links through sign-in can be checked by eye and by the e2e specs.
 */
export function FixturePage({
  hubOrigin,
  hostedPreview = false,
}: {
  hubOrigin: string;
  hostedPreview?: boolean;
}) {
  const access = useDestination();
  const product = PRODUCTS[access.productKey];
  const title = hostedPreview
    ? `${product.displayName} preview`
    : `${product.displayName} navigation fixture`;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bannerId = useId();
  const context = describeContext(access.context);

  useEffect(() => {
    document.title = `${title} | Trace`;
    headingRef.current?.focus({ preventScroll: true });
  }, [title]);

  return (
    <LoginShell
      brand={<ProductLockup productKey={access.productKey} surface="dark" />}
      tagline={PRODUCT_TAGLINES[access.productKey]}
    >
      <section className="trace-auth-dev" aria-labelledby={bannerId}>
        <div className="trace-auth-dev__header">
          <p id={bannerId} className="trace-auth-dev__title">
            {hostedPreview ? PREVIEW_BANNER : FIXTURE_BANNER}
          </p>
          <p className="trace-auth-dev__badge">{hostedPreview ? 'Preview' : 'Local only'}</p>
        </div>
        <p className="trace-auth-dev__notice">
          {hostedPreview
            ? `This page stands in for ${product.displayName} so that sign-in through Trace can be tried. It is not the product and holds no product data.`
            : `This page stands in for ${product.displayName} so that sign-in and navigation can be tried locally. It is not the product and holds no product data.`}
        </p>
      </section>

      <div className="trace-auth-header">
        <h1 ref={headingRef} tabIndex={-1} className="trace-auth-title">
          {title}
        </h1>
        <p className="trace-auth-lead">Trace confirmed your access to {product.displayName}.</p>
      </div>

      <dl className="trace-auth-details" aria-label="Confirmed access">
        <div>
          <dt>Signed in as</dt>
          <dd>
            {access.person.displayName}
            {access.person.email ? ` (${access.person.email})` : null}
          </dd>
        </div>
        <div>
          <dt>Organization</dt>
          <dd>{context.organization}</dd>
        </div>
        <div>
          <dt>Roles</dt>
          <dd>{context.roles}</dd>
        </div>
        <div>
          <dt>Opened at</dt>
          <dd>
            <code>{access.returnPath ?? 'no valid return path (default page)'}</code>
          </dd>
        </div>
        <div>
          <dt>Signed in with</dt>
          <dd>{access.mode === 'development' ? 'Development identity (local only)' : 'Auth0'}</dd>
        </div>
      </dl>

      <div className="trace-auth-stack">
        <a
          className={traceButtonClass('primary', { block: true })}
          href={hubUrl(hubOrigin, { route: 'login' })}
        >
          Back to the account hub
        </a>
        {access.context.type === 'employee' &&
        access.context.roles.some((role) => ACCOUNT_MANAGER_ROLES.has(role)) ? (
          // The account hub's pages for this organization (members, billing), for the roles the hub lets in: a member
          // following it would only meet the hub's refusal. The hub still decides what each role sees and signs the
          // person in silently when the Auth0 session exists; hubUrl has no settings route, so the path is joined here.
          <a
            className={traceButtonClass('quiet', { block: true })}
            href={new URL(
              `/settings/${encodeURIComponent(access.context.organization.id)}`,
              hubOrigin,
            ).toString()}
          >
            Account &amp; billing
          </a>
        ) : null}
        <a
          className={traceButtonClass('quiet', { block: true })}
          href={hubUrl(hubOrigin, { route: 'logout', productKey: access.productKey })}
        >
          Sign out
        </a>
      </div>
    </LoginShell>
  );
}
